import React from "react";
import { Link } from "react-router-dom";
import { SEO } from "../components/SEO";
import { COMPANY_INFO, SERVICES_LIST } from "../data/company";

export function AboutPage() {

  return (
    <div className="min-h-screen flex flex-col pt-28 pb-16 bg-[#f3f4f6]">
      <SEO
        title="Om Kokholm Byg - Kokholm Byg"
        description="Hos Kokholm Byg bygger vi på solidt håndværk, pålidelighed og lokal forankring. Vi udfører alt fra fliselægning og badeværelsesrenovering til reparationer, tilbygninger og nybyggeri."
      />

      {/* Page Banner */}
      <section 
        style={{ marginTop: '-40px' }}
        className="bg-[#1e2329] text-white py-14 border-b border-[#2d343f]"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#c06228] text-xs font-bold uppercase tracking-wider bg-[#272d36] px-3 py-1 rounded border border-[#373f4b]">
              Historie & Værdier
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mt-3 text-white">
              Om Kokholm Byg
            </h1>
            <p className="text-gray-300 text-sm sm:text-base mt-2">
              Lokal murer- og byggepartner i Holstebro med tradition for solidt håndværk og mere end 30 års erfaring.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            <div className="lg:col-span-8 bg-white p-6 sm:p-10 rounded border border-gray-200 shadow-sm space-y-8">
              <div>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-[#1e2329] mb-4">
                  Erfaring og jyske værdier
                </h2>
                <div className="prose prose-slate text-gray-700 leading-relaxed text-sm sm:text-base space-y-4 font-normal">
                  <p className="text-base sm:text-lg font-medium text-gray-900 border-l-4 border-[#c06228] pl-4 py-1 bg-[#fdf8f4] rounded-r">
                    Hos Kokholm Byg bygger vi på solidt håndværk, pålidelighed og lokal forankring. Vi udfører alt fra fliselægning og badeværelsesrenovering til reparationer, tilbygninger og nybyggeri.
                  </p>
                  <p>
                    Med mere end 30 års erfaring i byggebranchen har Kokholm Byg skabt et solidt fundament baseret på klassiske jyske værdier: Ærlighed, ordentlighed og respekt for det gode håndværk.
                  </p>
                  <p>
                    Hos os er en aftale altid en aftale. Vi lægger stor vægt på god og ærlig rådgivning fra start til slut, så du som kunde altid kender forløbet, tidsplanen og økonomien. Vi lover ikke mere, end vi kan holde – til gengæld holder vi altid det, vi lover.
                  </p>
                  <p className="font-semibold text-[#1e2329]">
                    Vi anser det tætte samarbejde og den direkte kontakt som helt afgørende for et vellykket resultat – med grundig rådgivning og personlig opfølgning på hver eneste opgave.
                  </p>
                </div>
              </div>

              {/* Jyske værdier fremhævet */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                <div className="bg-[#f9fafb] p-4 rounded border border-gray-200 text-center">
                  <div className="w-10 h-10 rounded-full bg-[#1e2329] text-[#c06228] flex items-center justify-center mx-auto mb-3 text-lg">
                    <i className="fa-solid fa-handshake" aria-hidden="true" />
                  </div>
                  <h4 className="font-bold text-[#1e2329] text-sm mb-1">En aftale er en aftale</h4>
                  <p className="text-xs text-gray-600">Troværdighed og klare linjer – vi leverer som aftalt til tiden.</p>
                </div>
                <div className="bg-[#f9fafb] p-4 rounded border border-gray-200 text-center">
                  <div className="w-10 h-10 rounded-full bg-[#1e2329] text-[#c06228] flex items-center justify-center mx-auto mb-3 text-lg">
                    <i className="fa-solid fa-comments" aria-hidden="true" />
                  </div>
                  <h4 className="font-bold text-[#1e2329] text-sm mb-1">Ærlig rådgivning</h4>
                  <p className="text-xs text-gray-600">Reelle vurderinger og gennemskuelige løsninger uden ubehagelige overraskelser.</p>
                </div>
                <div className="bg-[#f9fafb] p-4 rounded border border-gray-200 text-center">
                  <div className="w-10 h-10 rounded-full bg-[#1e2329] text-[#c06228] flex items-center justify-center mx-auto mb-3 text-lg">
                    <i className="fa-solid fa-medal" aria-hidden="true" />
                  </div>
                  <h4 className="font-bold text-[#1e2329] text-sm mb-1">30+ års erfaring</h4>
                  <p className="text-xs text-gray-600">Solid faglig ballast og gennemprøvede metoder i enhver opgave.</p>
                </div>
              </div>

              <div className="pt-6 border-t border-gray-100">
                <h2 className="text-xl sm:text-2xl font-extrabold text-[#1e2329] mb-6">
                  Vores primære arbejdsopgaver er:
                </h2>

                <ul className="space-y-4">
                  <li className="bg-[#f3f4f6] p-5 rounded border border-gray-200 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
                    <div className="w-12 h-12 rounded bg-[#1e2329] text-[#c06228] flex items-center justify-center text-xl shrink-0">
                      <i className="fa-solid fa-border-all" aria-hidden="true" />
                    </div>
                    <div className="space-y-1">
                      <h3 className="font-bold text-[#1e2329] text-base">Flisearbejde</h3>
                      <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                        Badeværelses renoveringer, rør skader og andre rum der ønskes beklædt eller frisket og med nye fliser.
                      </p>
                    </div>
                  </li>

                  <li className="bg-[#f3f4f6] p-5 rounded border border-gray-200 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
                    <div className="w-12 h-12 rounded bg-[#1e2329] text-[#c06228] flex items-center justify-center text-xl shrink-0">
                      <i className="fa-solid fa-house-chimney-crack" aria-hidden="true" />
                    </div>
                    <div className="space-y-1">
                      <h3 className="font-bold text-[#1e2329] text-base">Renovering</h3>
                      <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                        Køkken, bad, omforandring, gamle huse der trænger til en kærlig hånd eller nye input.
                      </p>
                    </div>
                  </li>

                  <li className="bg-[#f3f4f6] p-5 rounded border border-gray-200 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
                    <div className="w-12 h-12 rounded bg-[#1e2329] text-[#c06228] flex items-center justify-center text-xl shrink-0">
                      <i className="fa-solid fa-trowel-bricks" aria-hidden="true" />
                    </div>
                    <div className="space-y-1">
                      <h3 className="font-bold text-[#1e2329] text-base">Nybyg</h3>
                      <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                        Parcelhuse i 1 og 1½ plan.
                      </p>
                    </div>
                  </li>

                  <li className="bg-[#f3f4f6] p-5 rounded border border-gray-200 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
                    <div className="w-12 h-12 rounded bg-[#1e2329] text-[#c06228] flex items-center justify-center text-xl shrink-0">
                      <i className="fa-solid fa-hammer" aria-hidden="true" />
                    </div>
                    <div className="space-y-1">
                      <h3 className="font-bold text-[#1e2329] text-base">Reparation</h3>
                      <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                        Alt reparationsarbejde fra udskiftning af et par sten til almindelig vedligeholdelse af boligen.
                      </p>
                    </div>
                  </li>

                  <li className="bg-[#f3f4f6] p-5 rounded border border-gray-200 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
                    <div className="w-12 h-12 rounded bg-[#1e2329] text-[#c06228] flex items-center justify-center text-xl shrink-0">
                      <i className="fa-solid fa-square-plus" aria-hidden="true" />
                    </div>
                    <div className="space-y-1">
                      <h3 className="font-bold text-[#1e2329] text-base">Tilbyg</h3>
                      <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                        Er huset blevet for lille eller ønskes flere kvm. at blive inddraget til boligen er vi behjælpelige.
                      </p>
                    </div>
                  </li>
                </ul>
              </div>

            </div>

            {/* Sidebar info */}
            <div className="lg:col-span-4 space-y-6">
              <div className="bg-[#1e2329] text-white p-6 sm:p-8 rounded border border-[#2d343f] space-y-4">
                <h3 className="text-xl font-bold text-white border-b border-[#373f4b] pb-3">
                  Virksomhedsinformation
                </h3>
                <div className="space-y-3 text-sm text-gray-300">
                  <p className="font-semibold text-white">{COMPANY_INFO.name}</p>
                  <p>{COMPANY_INFO.address}</p>
                  <p>{COMPANY_INFO.city}</p>
                  <div className="pt-2 border-t border-[#373f4b] space-y-2">
                    <p className="flex items-center gap-2">
                      <i className="fa-solid fa-phone text-[#c06228]" aria-hidden="true" />
                      <a href={`tel:${COMPANY_INFO.phoneRaw}`} className="nowrap-contact hover:text-[#c06228] transition-colors">
                        <span className="whitespace-nowrap">{COMPANY_INFO.phone}</span>
                      </a>
                    </p>
                    <p className="flex items-center gap-2">
                      <i className="fa-solid fa-envelope text-[#c06228]" aria-hidden="true" />
                      <a href={`mailto:${COMPANY_INFO.email}`} className="nowrap-contact hover:text-[#c06228] transition-colors">
                        <span className="whitespace-nowrap">{COMPANY_INFO.email}</span>
                      </a>
                    </p>
                    <p className="text-xs font-mono text-gray-400 pt-1">
                      CVR: {COMPANY_INFO.cvr}
                    </p>
                  </div>
                </div>

                <div className="pt-4">
                  <Link
                    to="/kontakt"
                    className="btn-cta block text-center w-full py-3 px-4 font-semibold text-white rounded text-sm shadow"
                  >
                    Kontakt os
                  </Link>
                </div>
              </div>

              {/* Quick links to services */}
              <div className="bg-white p-6 rounded border border-gray-200 space-y-3">
                <h4 className="font-bold text-[#1e2329] text-base border-b border-gray-100 pb-2">
                  Se vores ydelser
                </h4>
                <ul className="space-y-2 text-sm">
                  {SERVICES_LIST.map((srv) => (
                    <li key={srv.slug}>
                      <Link
                        to={`/vi-tilbyder/${srv.slug.replace('vi-tilbyder-', '')}`}
                        className="text-gray-700 hover:text-[#c06228] flex items-center gap-2 transition-colors"
                      >
                        <i className="fa-solid fa-chevron-right text-[10px] text-[#c06228]" aria-hidden="true" />
                        <span>{srv.title}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}
